import React, { useEffect, useRef } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ChartOptions,
} from 'chart.js';
import style from './style.module.css';
import { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { SerializedError } from '@reduxjs/toolkit';
import { useTranslation } from 'react-i18next';
import { PreloaderSvg } from '../../../preloaderSvg/PreloaderSvg';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

interface TransportTypeData {
  transportTypeNameRu: string;
  transportTypeNameKy: string;
  year: string;
  totalCount: number;
}

interface YearlyData {
  [key: string]: TransportTypeData[];
}

interface ApiResponse {
  success: boolean;
  msg: string | null;
  res: YearlyData;
  time: string;
  ver: string;
}

interface VehiclesbyyeartableProps {
  data?: ApiResponse;
  isLoading?: boolean;
  error?: FetchBaseQueryError | SerializedError;
}

export const Vehiclesbyyeartable: React.FC<VehiclesbyyeartableProps> = ({
  data,
  isLoading,
  error
}) => {
  const { t, i18n } = useTranslation();
  const chartRef = useRef<HTMLCanvasElement | null>(null);
  const chartInstance = useRef<ChartJS | null>(null);

  const prepareChartData = () => {
   

    if (!data?.res) {
      return {
        labels: [],
        datasets: []
      };
    }

    
    const allTransportTypes = new Set<string>();
    Object.values(data.res).forEach(yearData => {
      yearData.forEach(item => {
        const typeName = i18n.language === 'ky' ? item.transportTypeNameKy : item.transportTypeNameRu;
        allTransportTypes.add(typeName);
      });
    });

    const transportTypes = Array.from(allTransportTypes);
    const years = Object.keys(data.res).sort();



    const datasets = years.map(year => {
      const yearData = data.res[year];
      return {
        label: year,
        data: transportTypes.map(type => {
          const item = yearData.find(d => 
            (i18n.language === 'ky' ? d.transportTypeNameKy : d.transportTypeNameRu) === type
          );
          return item ? item.totalCount : 0;
        }),
        backgroundColor: year === '2023' ? 'rgba(139, 195, 74, 0.8)' :
                        year === '2024' ? 'rgba(255, 193, 7, 0.8)' :
                        'rgba(76, 175, 80, 0.8)',
        borderColor: year === '2023' ? '#8BC34A' :
                    year === '2024' ? '#FFC107' :
                    '#4CAF50',
        borderWidth: 2,
        borderRadius: 8,
        barPercentage: 0.8,
        categoryPercentage: 0.8,
      };
    });

    const chartData = {
      labels: transportTypes,
      datasets
    };
    
  
    return chartData;
  };

  const options: ChartOptions<'bar'> = {
    responsive: true,
    maintainAspectRatio: false,
    layout: {
      padding: {
        bottom: 50
      }
    },
    scales: {
      x: {
        grid: {
          display: false
        },
        ticks: {
          font: {
            size: 12,
            weight: 'bold'
          },
          color: '#666',
          maxRotation: 0,
          minRotation: 0,
          padding: 20,
          autoSkip: false
        }
      },
      y: {
        beginAtZero: true,
        grid: {
          color: 'rgba(0, 0, 0, 0.1)',
          lineWidth: 0.5
        },
        border: {
          display: false
        },
        ticks: {
          font: {
            size: 12
          },
          color: '#666',
          padding: 10,
          callback: (tickValue: number | string) => {
            return tickValue + ' шт';
          }
        }
      }
    },
    plugins: {
      legend: {
        display: false
      },
      tooltip: {
        backgroundColor: 'rgba(255, 255, 255, 0.9)',
        titleColor: '#333',
        titleFont: {
          size: 14,
          weight: 'bold'
        },
        bodyColor: '#666',
        bodyFont: {
          size: 13
        },
        borderColor: 'rgba(0, 0, 0, 0.1)',
        borderWidth: 1,
        padding: 12,
        displayColors: true,
        callbacks: {
          title(tooltipItems) {
            return tooltipItems[0].label.replace(/\n/g, ' ');
          },
          label(tooltipItem) {
            return tooltipItem.dataset.label + ': ' + tooltipItem.parsed.y + ' шт';
          }
        }
      }
    },
    animation: {
      duration: 1000
    }
  };

  useEffect(() => {
    if (chartRef.current) {
      const ctx = chartRef.current.getContext('2d');
      
      if (ctx) {
        if (chartInstance.current) {
          chartInstance.current.destroy();
        }

        const chartData = prepareChartData();
        chartInstance.current = new ChartJS(ctx, {
          type: 'bar',
          data: chartData,
          options: options
        });
      }
    }

    return () => {
      if (chartInstance.current) {
        chartInstance.current.destroy();
      }
    };
  }, [data, i18n.language]);

  if (isLoading) {
    return <div className={style.container}><PreloaderSvg/></div>;
  }

  if (error) {
    return <div className={style.container}>Error loading data</div>;
  }

  return (
    <div className={style.container}>
      <h2 className={style.title}>{t("Number of equipment by year")}</h2>
      <div className={style.chartContainer}>
        <canvas ref={chartRef} />
      </div>
      <div className={style.legend}>
        {prepareChartData().datasets.map((dataset, index) => (
          <div key={index} className={style.legendItem}>
            <div 
              className={style.legendColor} 
              style={{ 
                backgroundColor: dataset.backgroundColor as string,
                border: `2px solid ${dataset.borderColor}`
              }} 
            />
            <span>{dataset.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
};